import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Field, Pill } from '@elysian/ui';
import type { Membership } from '../../data/source';
import { useSession } from '../../state/session';
import { AuthLayout } from './AuthLayout';

const ROLE: Record<Membership['role'], string> = {
  owner: 'Owner', admin: 'Admin', bookkeeper: 'Bookkeeper', accountant: 'Accountant', viewer: 'View only',
};

/** Choose which books to open, or set up the first set. */
export function Organisations() {
  const { me, status, chooseOrg, signOut, source, refresh, today } = useSession();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(status === 'no_books');
  const [name, setName] = useState('');
  const [where, setWhere] = useState<'IE' | 'UK'>('IE');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const orgs = me?.organisations ?? [];

  async function open(id: string) {
    await chooseOrg(id);
    navigate('/', { replace: true });
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const o = await source.createOrganisation({ legal_name: name.trim(), jurisdiction: where, first_year: today().getFullYear() });
      await refresh();
      if (useSession.getState().orgId !== o.id) await chooseOrg(o.id);
      navigate('/setup', { replace: true }); // new books start with the first-run setup
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={adding ? 'Set up your books' : 'Choose your books'}>
      {status === 'no_books' && orgs.length === 0 && (
        <p style={{ margin: 0 }}>
          You’re signed in as {me?.email ?? 'yourself'}, but you don’t have access to any books yet. If someone invited you,
          open the link in their email. Otherwise, set up your own below.
        </p>
      )}
      {!adding && (
        <ul className="el-org-list" aria-label="Your organisations">
          {orgs.map((o) => (
            <li key={o.id}>
              <button type="button" className="el-menu-item el-card" onClick={() => void open(o.id)}>
                <span><strong>{o.name}</strong><span className="el-hint" style={{ display: 'block' }}>{o.jurisdiction === 'UK' ? 'UK' : 'Ireland'} · {o.base_currency}</span></span>
                <span className="el-row" style={{ flexWrap: 'nowrap' }}>{o.read_only && <Pill tone="draft">Read only</Pill>}<Pill tone="info">{ROLE[o.role]}</Pill></span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <form className="el-stack" style={{ gap: 14 }} onSubmit={(e) => { e.preventDefault(); void create(); }}>
          <Field id="org-name" label="Business name" hint="The legal name, as registered">
            <input id="org-name" className="el-input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field id="org-where" label="Where it pays tax" hint="Sets the chart of accounts, VAT rates and the currency">
            <select id="org-where" className="el-input" value={where} onChange={(e) => setWhere(e.target.value as 'IE' | 'UK')}>
              <option value="IE">Ireland (euro, Revenue)</option>
              <option value="UK">United Kingdom (sterling, HMRC)</option>
            </select>
          </Field>
          <Button type="submit" variant="primary" disabled={busy || name.trim().length < 2}>Set up books</Button>
          {orgs.length > 0 && <Button variant="ghost" onClick={() => setAdding(false)}>Back to my books</Button>}
        </form>
      ) : (
        <Button variant="ghost" onClick={() => setAdding(true)}>Set up new books</Button>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
      <Button variant="ghost" onClick={() => void signOut().then(() => navigate('/sign-in', { replace: true }))}>Sign out</Button>
    </AuthLayout>
  );
}
